import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { CourseSlug } from "~/data/courses";

/**
 * The "Book a free call" modal is opened from the header, the hero, the AI
 * spotlight and every CTA banner, so its open state lives above the routes.
 */
type BookingContextValue = {
  isOpen: boolean;
  /** Preselects the course dropdown when opened from a course page. */
  presetCourse: CourseSlug | null;
  open: (presetCourse?: CourseSlug) => void;
  close: () => void;
};

const BookingContext = createContext<BookingContextValue | undefined>(
  undefined,
);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [presetCourse, setPresetCourse] = useState<CourseSlug | null>(null);

  const open = useCallback((course?: CourseSlug) => {
    setPresetCourse(course ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, presetCourse, open, close }),
    [isOpen, presetCourse, open, close],
  );

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking must be used within a BookingProvider");
  return ctx;
}
